import * as React from "react";

import { KeyValue } from "@skv/core";
import {
  OpportunitySearchQueryState,
  OrganisationSearchQueryState,
  useGlobal,
} from "@skv/state";

type AvailableFilters =
  | "categoryList"
  | "commitmentList"
  | "dayAndTimeList"
  | "suitableForList"
  | "typeOfWorkList";

interface UseFiltersOptions {
  searchQuery: OpportunitySearchQueryState | OrganisationSearchQueryState;
  filters?: AvailableFilters[];
  filterKeyToLabel?: (filter: AvailableFilters) => string;
  filterKeyToName?: (filter: AvailableFilters) => string;
  onChange: (
    query: OpportunitySearchQueryState | OrganisationSearchQueryState
  ) => void;
}

export type ResolvedFilter = {
  label: string;
  name: string;
  data: KeyValue[];
};

interface UseFiltersReturn {
  filters: ResolvedFilter[];
  handleFiltersChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectChange: (fieldName: string, value: number) => void;
  handleSelectClear: (fieldName: string) => void;
}

type UseFiltersHook = (options: UseFiltersOptions) => UseFiltersReturn;

export type FilterName =
  | "category"
  | "commitment"
  | "daytime"
  | "keyword"
  | "suitablefor"
  | "typeofwork"
  | "where";

const defaultFilterKeyToLabel = (filter: AvailableFilters): string => {
  switch (filter) {
    case "categoryList":
      return "Category";
    case "commitmentList":
      return "Commitment";
    case "dayAndTimeList":
      return "Day & Time";
    case "suitableForList":
      return "Suitable for";
    case "typeOfWorkList":
      return "Type of work";
    default:
      throw Error("Unknown filter key passed");
  }
};

const defaultFilterKeyToName = (filter: AvailableFilters): string => {
  switch (filter) {
    case "categoryList":
      return "category";
    case "commitmentList":
      return "commitment";
    case "dayAndTimeList":
      return "daytime";
    case "suitableForList":
      return "suitablefor";
    case "typeOfWorkList":
      return "typeofwork";
    default:
      throw Error("Unknown filter key passed");
  }
};

const defaultFilters: AvailableFilters[] = [
  "categoryList",
  "typeOfWorkList",
  "suitableForList",
  "commitmentList",
  "dayAndTimeList",
];

export const useSearchFilters: UseFiltersHook = ({
  searchQuery,
  filters = defaultFilters,
  filterKeyToLabel = defaultFilterKeyToLabel,
  filterKeyToName = defaultFilterKeyToName,
  onChange,
}) => {
  const global = useGlobal();

  const resolvedFilters: ResolvedFilter[] = React.useMemo(() => {
    return filters.map((n) => {
      return {
        label: filterKeyToLabel(n),
        name: filterKeyToName(n),
        data: global[n],
      };
    });
  }, [filterKeyToLabel, filterKeyToName, filters, global]);

  const handleFiltersChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const target = e.target;
    const nextSearchQuery = Object.assign({}, searchQuery);
    delete nextSearchQuery.page;
    const value = parseInt(e.target.value, 10);

    if (e.target.checked) {
      switch (true) {
        case Array.isArray(nextSearchQuery[target.name]):
          nextSearchQuery[target.name] = [
            ...nextSearchQuery[target.name],
            value,
          ];
          break;
        case !nextSearchQuery[target.name]:
        default:
          nextSearchQuery[target.name] = [value];
          break;
      }
    } else {
      switch (true) {
        case Array.isArray(nextSearchQuery[target.name]):
          nextSearchQuery[target.name] = nextSearchQuery[target.name].filter(
            (val) => val !== value
          );
          break;
        default:
          nextSearchQuery[target.name] = [];
          break;
      }
    }

    onChange(nextSearchQuery);
  };

  const handleSelectChange = (fieldName: string, value: number): void => {
    const nextSearchQuery = Object.assign({}, searchQuery);
    delete nextSearchQuery.page;

    if (Array.isArray(nextSearchQuery[fieldName])) {
      if (nextSearchQuery[fieldName].includes(value)) {
        nextSearchQuery[fieldName] = nextSearchQuery[fieldName].filter(
          (n) => n !== value
        );
      } else {
        nextSearchQuery[fieldName] = [...nextSearchQuery[fieldName], value];
      }
    } else {
      nextSearchQuery[fieldName] = [value];
    }

    onChange(nextSearchQuery);
  };

  const handleSelectClear = (fieldName: string): void => {
    const nextSearchQuery = Object.assign({}, searchQuery);
    delete nextSearchQuery.page;

    nextSearchQuery[fieldName] = [];
    onChange(nextSearchQuery);
  };

  return {
    filters: resolvedFilters,
    handleFiltersChange,
    handleSelectChange,
    handleSelectClear,
  };
};
